import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  Button,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Pagination,
  Stack,
  Avatar,
  Divider
} from '@mui/material';
import {
  Search,
  CalendarToday,
  Person,
  Visibility,
  Bookmark,
  Share,
  FilterList,
  TrendingUp,
  NewReleases,
  Category
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

// Sample blog data
const blogPosts = [
  {
    id: 1,
    title: 'Complete Guide to GST Compliance for Indian Businesses',
    excerpt: 'Learn everything you need to know about GST compliance, filing procedures, and best practices for Indian businesses.',
    content: 'GST compliance is crucial for Indian businesses. This comprehensive guide covers all aspects...',
    author: 'AkMo Team',
    authorAvatar: '/avatars/akmo-team.jpg',
    publishDate: '2024-01-15',
    readTime: '8 min read',
    category: 'GST & Compliance',
    tags: ['GST', 'Compliance', 'Tax', 'Business'],
    image: '/blog-images/gst-guide.jpg',
    views: 1250,
    likes: 89,
    featured: true
  },
  {
    id: 2,
    title: 'How to Choose the Right Accounting Software for Your Business',
    excerpt: 'Discover the key factors to consider when selecting accounting software that fits your business needs.',
    content: 'Choosing the right accounting software can make or break your business operations...',
    author: 'Sarah Johnson',
    authorAvatar: '/avatars/sarah.jpg',
    publishDate: '2024-01-12',
    readTime: '6 min read',
    category: 'Software',
    tags: ['Accounting', 'Software', 'Business Tools'],
    image: '/blog-images/accounting-software.jpg',
    views: 980,
    likes: 67,
    featured: false
  },
  {
    id: 3,
    title: 'Understanding TDS and Its Impact on Your Business',
    excerpt: 'A detailed explanation of Tax Deducted at Source (TDS) and how it affects your business operations.',
    content: 'TDS is an important tax mechanism that affects many business transactions...',
    author: 'Rajesh Kumar',
    authorAvatar: '/avatars/rajesh.jpg',
    publishDate: '2024-01-10',
    readTime: '7 min read',
    category: 'Taxation',
    tags: ['TDS', 'Tax', 'Deduction', 'Business'],
    image: '/blog-images/tds-guide.jpg',
    views: 756,
    likes: 45,
    featured: false
  },
  {
    id: 4,
    title: 'Digital Transformation in Indian Accounting',
    excerpt: 'How digital transformation is revolutionizing accounting practices in Indian businesses.',
    content: 'The accounting industry in India is undergoing a massive digital transformation...',
    author: 'Priya Sharma',
    authorAvatar: '/avatars/priya.jpg',
    publishDate: '2024-01-08',
    readTime: '9 min read',
    category: 'Digital Transformation',
    tags: ['Digital', 'Transformation', 'Technology', 'Accounting'],
    image: '/blog-images/digital-transformation.jpg',
    views: 1100,
    likes: 78,
    featured: true
  },
  {
    id: 5,
    title: 'Financial Planning Tips for Small Businesses',
    excerpt: 'Essential financial planning strategies to help small businesses grow and succeed.',
    content: 'Financial planning is the backbone of any successful small business...',
    author: 'Amit Patel',
    authorAvatar: '/avatars/amit.jpg',
    publishDate: '2024-01-05',
    readTime: '5 min read',
    category: 'Financial Planning',
    tags: ['Financial Planning', 'Small Business', 'Growth'],
    image: '/blog-images/financial-planning.jpg',
    views: 890,
    likes: 56,
    featured: false
  },
  {
    id: 6,
    title: 'Invoice Management Best Practices',
    excerpt: 'Learn the best practices for managing invoices efficiently and improving cash flow.',
    content: 'Effective invoice management is crucial for maintaining healthy cash flow...',
    author: 'Sunita Reddy',
    authorAvatar: '/avatars/sunita.jpg',
    publishDate: '2024-01-03',
    readTime: '6 min read',
    category: 'Invoice Management',
    tags: ['Invoicing', 'Cash Flow', 'Business Operations'],
    image: '/blog-images/invoice-management.jpg',
    views: 650,
    likes: 42,
    featured: false
  }
];

const categories = [
  'All',
  'GST & Compliance',
  'Software',
  'Taxation',
  'Digital Transformation',
  'Financial Planning',
  'Invoice Management'
];

const Blogs = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  // Filter posts based on search and category
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const paginatedPosts = filteredPosts.slice(startIndex, startIndex + postsPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ overflow: 'hidden' }}>      
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                variant="h2"
                fontWeight="bold"
                gutterBottom
                sx={{
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                AkMo Blog
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}
              >
                Insights, tips, and guides for Indian businesses
              </Typography>
            </motion.div>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <TextField
                fullWidth
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  maxWidth: '600px',
                  mx: 'auto',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    backgroundColor: 'white',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search color="primary" />
                    </InputAdornment>
                  ),
                }}
              />
            </motion.div>
          </Box>
        </Container>
      </Box>

      {/* Categories Filter */}
      <Box sx={{ py: 4, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 4 }}>
            {categories.map((category) => (
              <Chip
                key={category}
                label={category}
                onClick={() => handleCategoryChange(category)}
                variant={selectedCategory === category ? 'filled' : 'outlined'}
                sx={{
                  backgroundColor: selectedCategory === category ? 'primary.main' : 'transparent',
                  color: selectedCategory === category ? 'white' : 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: selectedCategory === category ? 'primary.dark' : 'primary.light',
                    color: 'white',
                  },
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Box>
        </Container>
      </Box>

      {/* Blog Posts */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {paginatedPosts.map((post, index) => (
              <Grid item xs={12} md={6} lg={4} key={post.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                      }
                    }}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={post.image}
                      alt={post.title}
                      sx={{
                        borderRadius: '16px 16px 0 0',
                        objectFit: 'cover'
                      }}
                    />
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Chip
                          label={post.category}
                          size="small"
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'white',
                            fontWeight: 'bold'
                          }}
                        />
                        {post.featured && (
                          <Chip
                            label="Featured"
                            size="small"
                            sx={{
                              ml: 1,
                              backgroundColor: '#FF6B35',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          />
                        )}
                      </Box>

                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        gutterBottom
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 2,
                          lineHeight: 1.4
                        }}
                      >
                        {post.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          mb: 3,
                          lineHeight: 1.6
                        }}
                      >
                        {post.excerpt}
                      </Typography>

                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          src={post.authorAvatar}
                          sx={{ width: 32, height: 32, mr: 1 }}
                        >
                          {post.author.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {post.author}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(post.publishDate).toLocaleDateString()} • {post.readTime}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Visibility sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {post.views}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {post.likes} likes
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<Bookmark />}>
                            Save
                          </Button>
                          <Button size="small" startIcon={<Share />}>
                            Share
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    fontWeight: 'bold'
                  }
                }}
              />
            </Box>
          )}
        </Container>
      </Box>

      {/* Newsletter Signup */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'white' }}>
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box textAlign="center">
              <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{
                  mb: 2,
                  background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
                  backgroundClip: 'text',
                  color: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Stay Updated
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                Get the latest insights and tips delivered to your inbox
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, maxWidth: '500px', mx: 'auto' }}>
                <TextField
                  fullWidth
                  placeholder="Enter your email"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '50px',
                    }
                  }}
                />
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: '50px',
                    px: 4,
                    fontWeight: 'bold',
                    background: 'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
                    '&:hover': {
                      boxShadow: '0 8px 25px rgba(71, 118, 230, 0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Subscribe
                </Button>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>    </Box>
  );
};

export default Blogs;
